/**
 * InvitationCodeListPage
 * List page for invitation code management
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/utils";
import { Plus, Eye, Ban, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import {
  useInvitationCodes,
  useRevokeInvitationCode,
} from "@/hooks/useInvitationCodeQueries";
import type { InvitationCodeStatus } from "@/types/invitation-code";

export function InvitationCodeListPage() {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<
    InvitationCodeStatus | "All"
  >("All");
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: response, isLoading } = useInvitationCodes({
    status: selectedStatus !== "All" ? selectedStatus : undefined,
    page,
    limit,
  });

  const revokeMutation = useRevokeInvitationCode();

  const [revokeDialog, setRevokeDialog] = useState<{
    open: boolean;
    codeId: string;
    code: string;
  }>({
    open: false,
    codeId: "",
    code: "",
  });

  const invitationCodes = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const totalCount = response?.pagination?.totalItems || 0;

  const handleRevoke = (codeId: string, code: string) => {
    setRevokeDialog({ open: true, codeId, code });
  };

  const confirmRevoke = () => {
    revokeMutation.mutate(revokeDialog.codeId, {
      onSuccess: () => {
        setRevokeDialog({ open: false, codeId: "", code: "" });
      },
    });
  };

  const getStatusColor = (status: InvitationCodeStatus) => {
    switch (status) {
      case "Active":
        return "default";
      case "Paused":
        return "secondary";
      case "Expired":
        return "outline";
      default:
        return "outline";
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invitation Codes"
        subtitle="Manage invitation codes for new user registration"
        actions={[
          {
            label: "Create Code",
            onClick: () => navigate("/admin/invitation-codes/new"),
            icon: <Plus className="mr-2 h-4 w-4" />,
          },
        ]}
      />

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter invitation codes by status</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={selectedStatus}
            onValueChange={(value) => {
              setSelectedStatus(value as InvitationCodeStatus | "All");
              setPage(1);
            }}
          >
            <TabsList>
              <TabsTrigger value="All">All</TabsTrigger>
              <TabsTrigger value="Active">Active</TabsTrigger>
              <TabsTrigger value="Paused">Paused</TabsTrigger>
              <TabsTrigger value="Expired">Expired</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Invitation Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle>Invitation Codes ({totalCount})</CardTitle>
          <CardDescription>
            {selectedStatus === "All"
              ? "All invitation codes"
              : `${selectedStatus} invitation codes`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">Loading...</span>
            </div>
          ) : invitationCodes.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No invitation codes found</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/admin/invitation-codes/new")}
              >
                Create your first invitation code
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reward</TableHead>
                      <TableHead>Usage</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitationCodes.map((code) => (
                      <TableRow
                        key={code.id}
                        className="cursor-pointer hover:bg-muted/50"
                      >
                        <TableCell className="font-mono font-medium">
                          {code.code}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusColor(code.status)}>
                            {code.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Intl.NumberFormat("vi-VN").format(
                            code.rewardAmount,
                          )}{" "}
                          {code.currency}
                        </TableCell>
                        <TableCell>
                          {code.usageCount} / {code.maxUses ?? "∞"}
                        </TableCell>
                        <TableCell>{formatDate(code.createdAt)}</TableCell>
                        <TableCell>
                          {code.validTo ? formatDate(code.validTo) : "Never"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(`/admin/invitation-codes/${code.id}`)
                              }
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {code.status === "Active" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevoke(code.id, code.code)}
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog
        open={revokeDialog.open}
        onOpenChange={(open) => setRevokeDialog((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation Code</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to revoke invitation code{" "}
              <strong>{revokeDialog.code}</strong>? This action cannot be undone
              and the code will no longer be valid for registration.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
